import type { components } from '@/generated/contracts/api-v1';
import type { TemplateUsageSummary } from '@/types/templateSubmissionTypes';
import { v1, toResult } from '@/infrastructure/api/v1/client';

/** One of the creator's projects with its stories, scene versions and images (listCatalogueProjects). */
export type CreatorProject = components['schemas']['CatalogueProjectDto'];

export interface CreatorProjectPage {
  page: number;
  pageSize: number;
  totalProjects: number;
  projects: CreatorProject[];
}

/** The dashboard's sort orders as listCatalogueProjects names them. */
const SORTS: Record<string, string> = { newest: '-updatedAt', oldest: 'updatedAt', asc: 'displayName', desc: '-displayName' };

/** One page (1-based) of the signed-in creator's projects, every status, with how many there are in all. */
export async function fetchMyProjects(params: {
  page: number;
  pageSize: number;
  search?: string;
  sort?: string;
}): Promise<CreatorProjectPage | null> {
  const search = params.search?.trim() || undefined;
  const sort = SORTS[params.sort ?? 'newest'] ?? SORTS.newest;
  const { data, error } = await toResult(v1.GET('/api/v1/catalogue-projects', {
    params: { query: { scope: 'mine', search, sort, limit: params.pageSize, offset: (params.page - 1) * params.pageSize } },
  }));
  if (error) {
    console.error('[projects] creator list failed', error.message);
    return null;
  }
  return { page: params.page, pageSize: params.pageSize, totalProjects: data.total, projects: data.items };
}

// Null when the caller is not the template's creator (404); throws when the read fails, which is not zero uses.
export async function fetchTemplateUsageSummary(sceneOriginId: string): Promise<TemplateUsageSummary | null> {
  const { data, error } = await toResult(v1.GET('/api/v1/stories/{storyUniqueId}/template-usage', { params: { path: { storyUniqueId: sceneOriginId } } }));
  if (error) {
    if (error.status === 404) return null;
    throw error;
  }
  return { qualifyingUseCount: data.qualifyingUseCount, totalUseCount: data.totalUseCount };
}
